import { useMemo } from 'react';
import type { Clip } from '@cinegrid/core';
import { isScaleLinked, syncLinkedScaleKeyframes } from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { useAnimatableParam } from '../hooks/useAnimatableParam';
import { PanelShell, ScaleLinkButton, SectionLabel, SliderRow } from '../controls/PanelShell';

function TransformControls({ clip }: { clip: Clip }) {
  const updateClipTransform = useEditorStore((s) => s.updateClipTransform);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);
  const scaleLinked = isScaleLinked(clip.properties);

  const t = clip.properties.transform;
  const set = (partial: Parameters<typeof updateClipTransform>[1]) =>
    updateClipTransform(clip.id, partial);

  const positionX = useAnimatableParam(clip.id, 'positionX', t.positionX, (v) =>
    set({ positionX: v })
  );
  const positionY = useAnimatableParam(clip.id, 'positionY', t.positionY, (v) =>
    set({ positionY: v })
  );
  const scaleX = useAnimatableParam(clip.id, 'scaleX', t.scaleX, (v) => set({ scaleX: v }));
  const scaleY = useAnimatableParam(clip.id, 'scaleY', t.scaleY, (v) => set({ scaleY: v }));
  const scaleUniform = useAnimatableParam(
    clip.id,
    ['scaleX', 'scaleY'],
    (t.scaleX + t.scaleY) / 2,
    (v) => set({ scaleX: v, scaleY: v })
  );
  const rotation = useAnimatableParam(clip.id, 'rotation', t.rotation, (v) =>
    set({ rotation: v })
  );
  const anchorX = useAnimatableParam(clip.id, 'anchorX', t.anchorX, (v) => set({ anchorX: v }));
  const anchorY = useAnimatableParam(clip.id, 'anchorY', t.anchorY, (v) => set({ anchorY: v }));
  const opacity = useAnimatableParam(clip.id, 'opacity', t.opacity, (v) => set({ opacity: v }));

  return (
    <PanelShell title="Transform">
      <SectionLabel>Position</SectionLabel>
      <SliderRow
        label="X"
        value={t.positionX}
        min={-1}
        max={1}
        defaultValue={0}
        keyframed={positionX.keyframed}
        onToggleKeyframe={positionX.onToggleKeyframe}
        onChange={positionX.onChange}
      />
      <SliderRow
        label="Y"
        value={t.positionY}
        min={-1}
        max={1}
        defaultValue={0}
        keyframed={positionY.keyframed}
        onToggleKeyframe={positionY.onToggleKeyframe}
        onChange={positionY.onChange}
      />
      <SectionLabel>Scale</SectionLabel>
      {scaleLinked ? (
        <SliderRow
          label="Scale"
          value={(t.scaleX + t.scaleY) / 2}
          min={0.1}
          max={3}
          defaultValue={1}
          keyframed={scaleUniform.keyframed}
          onToggleKeyframe={scaleUniform.onToggleKeyframe}
          onChange={scaleUniform.onChange}
        />
      ) : (
        <>
          <SliderRow
            label="Scale X"
            value={t.scaleX}
            min={0.1}
            max={3}
            defaultValue={1}
            keyframed={scaleX.keyframed}
            onToggleKeyframe={scaleX.onToggleKeyframe}
            onChange={scaleX.onChange}
          />
          <SliderRow
            label="Scale Y"
            value={t.scaleY}
            min={0.1}
            max={3}
            defaultValue={1}
            keyframed={scaleY.keyframed}
            onToggleKeyframe={scaleY.onToggleKeyframe}
            onChange={scaleY.onChange}
          />
        </>
      )}
      <ScaleLinkButton
        linked={scaleLinked}
        onToggle={() => {
          const next = !scaleLinked;
          if (next) {
            const avg = (t.scaleX + t.scaleY) / 2;
            set({ scaleX: avg, scaleY: avg });
            updateClipProperties(clip.id, {
              scaleLinked: true,
              keyframes: syncLinkedScaleKeyframes(clip.properties.keyframes),
            });
          } else {
            updateClipProperties(clip.id, { scaleLinked: false });
          }
        }}
      />
      <SectionLabel>Rotation / Anchor</SectionLabel>
      <SliderRow
        label="Rotation"
        value={t.rotation}
        min={-180}
        max={180}
        step={0.5}
        defaultValue={0}
        format={(v) => `${v.toFixed(1)}°`}
        keyframed={rotation.keyframed}
        onToggleKeyframe={rotation.onToggleKeyframe}
        onChange={rotation.onChange}
      />
      <SliderRow
        label="Anchor X"
        value={t.anchorX}
        min={0}
        max={1}
        defaultValue={0.5}
        keyframed={anchorX.keyframed}
        onToggleKeyframe={anchorX.onToggleKeyframe}
        onChange={anchorX.onChange}
      />
      <SliderRow
        label="Anchor Y"
        value={t.anchorY}
        min={0}
        max={1}
        defaultValue={0.5}
        keyframed={anchorY.keyframed}
        onToggleKeyframe={anchorY.onToggleKeyframe}
        onChange={anchorY.onChange}
      />
      <SectionLabel>Opacity</SectionLabel>
      <SliderRow
        label="Opacity"
        value={t.opacity}
        min={0}
        max={1}
        defaultValue={1}
        keyframed={opacity.keyframed}
        onToggleKeyframe={opacity.onToggleKeyframe}
        onChange={opacity.onChange}
      />
    </PanelShell>
  );
}

export function TransformPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0]),
    [project.clips, selection]
  );

  if (!clip) {
    return (
      <PanelShell title="Transform">
        <p className="p-3 text-2xs text-text-low">Select a clip.</p>
      </PanelShell>
    );
  }

  return <TransformControls clip={clip} />;
}
